import { displayPhenomenon, severityColor, sourceLabel, voivodeshipName } from "./constants";
import { escapeHtml } from "./html";
import {
  alertsForRegion,
  alertsForVoivodeship,
  alertsNationwide,
  alertStatus,
  boundariesByTeryt,
  countyNameByTeryt,
  formatDateRange,
  sortAlerts,
} from "./state";
import type { Alert } from "./types";
import { clearCountyHighlight, zoomToVoivodeship } from "./mapView";

type Mode = "region" | "all";

const MAX_SCOPE_LABELS_SHOWN = 8;
const MAX_COUNTIES_PER_VOIVODESHIP_LINE = 5;

let selectedRegionCode: string | null = null;
let mode: Mode = "region";

let regionNameEl: HTMLElement;
let alertsContainerEl: HTMLElement;
let panelEl: HTMLElement;
let regionModeButton: HTMLButtonElement;
let allModeButton: HTMLButtonElement;
let closeButtonEl: HTMLButtonElement;

function groupAlertsByContent(alerts: Alert[]): Alert[][] {
  const groups = new Map<string, Alert[]>();
  for (const alert of alerts) {
    const key = [alert.category, alert.phenomenon, alert.severity, alert.starts_at, alert.ends_at, alert.description].join(
      "|",
    );
    const group = groups.get(key);
    if (group) {
      group.push(alert);
    } else {
      groups.set(key, [alert]);
    }
  }
  return [...groups.values()];
}

function countyIdsFullyCoverVoivodeship(prefix: string, countyIds: string[]): boolean {
  const allCountyIds = [...boundariesByTeryt.keys()].filter((code) => code.startsWith(prefix));
  return allCountyIds.length > 0 && allCountyIds.every((code) => countyIds.includes(code));
}

function groupCountyIdsByVoivodeship(alerts: Alert[]): Map<string, string[]> {
  const countyIdsByVoivodeship = new Map<string, string[]>();
  for (const alert of alerts) {
    if (alert.scope_kind !== "county") continue;
    const prefix = alert.scope_id.slice(0, 2);
    const list = countyIdsByVoivodeship.get(prefix) ?? [];
    if (!list.includes(alert.scope_id)) list.push(alert.scope_id);
    countyIdsByVoivodeship.set(prefix, list);
  }
  return countyIdsByVoivodeship;
}

function voivodeshipScopeLabels(alerts: Alert[]): string[] {
  const labels: string[] = [];
  for (const alert of alerts) {
    if (alert.scope_kind !== "voivodeship") continue;
    const label = `woj. ${voivodeshipName(alert.scope_id)}`;
    if (!labels.includes(label)) labels.push(label);
  }
  return labels;
}

/** Flat county list, used when a single voivodeship is already established by the panel header. */
function scopeSummaryFlat(alerts: Alert[]): string {
  const countyIdsByVoivodeship = groupCountyIdsByVoivodeship(alerts);
  const countyLabels: string[] = [];
  for (const [prefix, countyIds] of countyIdsByVoivodeship) {
    if (countyIdsFullyCoverVoivodeship(prefix, countyIds)) {
      countyLabels.push(`całe woj. ${voivodeshipName(prefix)}`);
    } else {
      for (const countyId of countyIds) {
        countyLabels.push(`powiat ${countyNameByTeryt.get(countyId) ?? countyId}`);
      }
    }
  }

  const labels = [...countyLabels, ...voivodeshipScopeLabels(alerts)];
  if (labels.length > MAX_SCOPE_LABELS_SHOWN) {
    return labels.slice(0, MAX_SCOPE_LABELS_SHOWN).join(", ") + ` i ${labels.length - MAX_SCOPE_LABELS_SHOWN} innych`;
  }
  return labels.join(", ");
}

/** One line per affected voivodeship, so it's clear which counties belong to which — used nationwide. */
interface ScopeLine {
  heading: string;
  detail: string | null;
}

function scopeSummaryByVoivodeship(alerts: Alert[]): ScopeLine[] {
  const countyIdsByVoivodeship = groupCountyIdsByVoivodeship(alerts);
  const lines: ScopeLine[] = [];

  for (const [prefix, countyIds] of countyIdsByVoivodeship) {
    const name = voivodeshipName(prefix);
    if (countyIdsFullyCoverVoivodeship(prefix, countyIds)) {
      lines.push({ heading: `całe woj. ${name}`, detail: null });
      continue;
    }
    const names = countyIds.map((id) => countyNameByTeryt.get(id) ?? id);
    const shown = names.slice(0, MAX_COUNTIES_PER_VOIVODESHIP_LINE);
    const rest = names.length - shown.length;
    const countyText = shown.map((n) => `powiat ${n}`).join(", ") + (rest > 0 ? ` i ${rest} innych` : "");
    lines.push({ heading: `woj. ${name}`, detail: countyText });
  }

  lines.push(...voivodeshipScopeLabels(alerts).map((label) => ({ heading: label, detail: null })));
  return lines;
}

function scopeHtml(scope: ScopeLine | string | null): string {
  if (!scope) return "";
  if (typeof scope === "string") {
    return `<div class="alert-card-scope">${escapeHtml(scope)}</div>`;
  }
  const detail = scope.detail ? `<div class="alert-card-scope-detail">${escapeHtml(scope.detail)}</div>` : "";
  return `<div class="alert-card-scope"><div class="alert-card-scope-heading">${escapeHtml(scope.heading)}</div>${detail}</div>`;
}

export function singleCardHtml(first: Alert, now: Date, scope: ScopeLine | string | null): string {
  const status = alertStatus(first, now);
  // Severity degrees (I/II/III stopnia) are an IMGW concept; Open-Meteo threshold
  // rules always carry severity=1 as a plain "active" flag, not a real degree —
  // matches the same distinction already made for Discord notifications.
  const levelText = first.severity > 0 && first.source !== "open_meteo" ? ` st. ${first.severity}` : "";
  return `
    <div class="alert-card" style="border-left-color: ${severityColor(first.severity)}">
      <div class="alert-card-title-row">
        <span class="alert-card-title">${escapeHtml(displayPhenomenon(first.phenomenon))}${levelText}</span>
        <div class="alert-card-badges">
          <span class="alert-card-source alert-card-source-${first.source}">${sourceLabel(first.source)}</span>
          <span class="alert-card-status ${status === "aktywne" ? "active" : ""}">${status}</span>
        </div>
      </div>
      ${scopeHtml(scope)}
      <div class="alert-card-dates">${formatDateRange(first.starts_at, first.ends_at)}</div>
      <div class="alert-card-desc">${escapeHtml(first.description)}</div>
    </div>
  `;
}

function buildCardsHtml(group: Alert[], now: Date, showLocation: boolean, groupByVoivodeship: boolean): string[] {
  const first = group[0];
  if (!showLocation) {
    return [singleCardHtml(first, now, null)];
  }
  if (!groupByVoivodeship) {
    return [singleCardHtml(first, now, scopeSummaryFlat(group))];
  }
  return scopeSummaryByVoivodeship(group).map((line) => singleCardHtml(first, now, line));
}

function updateModeButtons(): void {
  regionModeButton.classList.toggle("active", mode === "region");
  allModeButton.classList.toggle("active", mode === "all");
  regionModeButton.disabled = !selectedRegionCode;
}

let lastFocusedBeforeRegion: HTMLElement | null = null;

export function isOpen(): boolean {
  return !panelEl.classList.contains("hidden");
}

export function hideRegionDetail(): void {
  const wasOpen = isOpen();
  panelEl.classList.add("hidden");
  clearCountyHighlight();
  if (wasOpen) lastFocusedBeforeRegion?.focus();
}

export function renderRegionDetail(): void {
  const showAll = mode === "all" || !selectedRegionCode;
  const prefix = selectedRegionCode ? selectedRegionCode.slice(0, 2) : null;

  let alerts: Alert[];
  let headerName: string;
  if (showAll) {
    if (prefix) {
      alerts = alertsForVoivodeship(prefix);
      headerName = voivodeshipName(prefix);
    } else {
      alerts = alertsNationwide();
      headerName = "Wszystkie ostrzeżenia";
    }
  } else {
    alerts = alertsForRegion(selectedRegionCode!);
    headerName = countyNameByTeryt.get(selectedRegionCode!) ?? selectedRegionCode!;
  }

  regionNameEl.textContent = headerName;

  const now = new Date();
  const sorted = sortAlerts(alerts);
  const groups = showAll ? groupAlertsByContent(sorted) : sorted.map((alert) => [alert]);
  const groupByVoivodeship = showAll && !prefix;
  alertsContainerEl.innerHTML =
    groups.flatMap((group) => buildCardsHtml(group, now, showAll, groupByVoivodeship)).join("") ||
    '<div class="alert-card-empty">Brak ostrzeżeń.</div>';

  updateModeButtons();
  const wasHidden = panelEl.classList.contains("hidden");
  panelEl.classList.remove("hidden");
  if (wasHidden) {
    lastFocusedBeforeRegion = document.activeElement as HTMLElement | null;
    closeButtonEl.focus();
  }
}

export function showRegionDetail(terytCode: string): void {
  selectedRegionCode = terytCode;
  mode = "region";
  renderRegionDetail();
}

export function showAllAlertsNationwide(): void {
  selectedRegionCode = null;
  mode = "all";
  renderRegionDetail();
}

export function initRegionDetail(ids: {
  panel: string;
  regionName: string;
  alertsContainer: string;
  closeButton: string;
  modeRegionButton: string;
  modeAllButton: string;
}): void {
  panelEl = document.getElementById(ids.panel) as HTMLElement;
  regionNameEl = document.getElementById(ids.regionName) as HTMLElement;
  alertsContainerEl = document.getElementById(ids.alertsContainer) as HTMLElement;
  regionModeButton = document.getElementById(ids.modeRegionButton) as HTMLButtonElement;
  allModeButton = document.getElementById(ids.modeAllButton) as HTMLButtonElement;
  const closeButton = document.getElementById(ids.closeButton) as HTMLButtonElement;
  closeButtonEl = closeButton;

  closeButton.addEventListener("click", hideRegionDetail);

  regionModeButton.addEventListener("click", () => {
    if (!selectedRegionCode) return;
    mode = "region";
    renderRegionDetail();
  });

  allModeButton.addEventListener("click", () => {
    mode = "all";
    if (selectedRegionCode) {
      zoomToVoivodeship(selectedRegionCode.slice(0, 2));
    }
    renderRegionDetail();
  });
}
